/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useRef, useEffect } from 'react';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

interface UniversalDropSpacerProps {
  index: number;
  groupIndex: number | null;
  onDropCard: (
    fromId: string,
    toIndex: number,
    targetGroupIndex: number | null
  ) => void;
  onDropGroup?: (fromId: string, toIndex: number) => void;
  active?: boolean;
  draggingCardId?: string | null;
  draggingGroupId?: string | null;
}

export const UniversalDropSpacer: React.FC<UniversalDropSpacerProps> = ({
  index,
  groupIndex,
  onDropCard,
  onDropGroup,
  active,
  draggingCardId,
  draggingGroupId,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const cleanup = dropTargetForElements({
      element,
      getData: () => ({
        index,
        groupIndex,
        type: draggingCardId ? 'card' : 'group',
      }),
      canDrop: ({ source }) => {
        const sourceType = source.data?.type;
        return sourceType === 'card' || sourceType === 'group';
      },
      onDragEnter: ({ self }) => {
        if (self instanceof HTMLElement) {
          self.setAttribute('data-highlight', 'true');
        }
      },
      onDragLeave: ({ self }) => {
        if (self instanceof HTMLElement) {
          self.removeAttribute('data-highlight');
        }
      },
      onDrop: ({ source }) => {
        const fromId = source.data?.id;
        const type = source.data?.type;

        if (!fromId || !type) return;

        if (type === 'card') {
          onDropCard(fromId.toString(), index, groupIndex);
        } else if (type === 'group' && groupIndex === null && onDropGroup) {
          onDropGroup(fromId.toString(), index);
        }
      },
    });

    return () => cleanup?.();
  }, [
    index,
    groupIndex,
    onDropCard,
    onDropGroup,
    draggingCardId,
    draggingGroupId,
  ]);

  return (
    <div
      ref={ref}
      css={{
        height: active ? '24px' : '10px',
        margin: '4px 0',
        backgroundColor: active ? 'rgba(17,255,0,0.3)' : 'transparent',
        border: active ? '1px dashed green' : '1px dashed transparent',
        borderRadius: '6px',
        transition: 'all 0.2s ease',
        '&[data-highlight]': {
          backgroundColor: 'rgba(17,255,0,0.5)',
          border: '1px dashed green',
        },
      }}
    />
  );
};
